import { ITestingLoginResponse } from "./Models/ITestingLoginResponse";

export interface IAjaxHelper {
    postRequest<T>(Url: string, Data?: any): Promise<T>
    getRequest<T>(Url: string, Data?: Record<string, (string | number | boolean)>): Promise<T>
    runInSequence<T, TPromise>(values: Array<T>, funcToRun: (value: T) => Promise<TPromise>, funcAfterEachRun?: (value: TPromise) => boolean | any | undefined, funcWhenFinished?: () => any | undefined): void
}

export class AjaxHelperTesting implements IAjaxHelper {
    sessionId: string = "";

    constructor() {
        fetch("https://physicsclassroom.hbstest.net/api/tasktracker/questions/TestingUserLogin", {
            method: "POST",
            mode: 'cors',
            credentials: "include",
            cache: "no-cache",
            headers: {
                "Authorization": "Bearer " + this.sessionId,
                'Content-Type': 'application/json',
            }
        }).then(x => {
            return x.json();
        }).then(x => {
            var response = x as ITestingLoginResponse;
            if (response.successful) {
                this.sessionId = response.SessionID;
            } else {
                alert("Error setting up testing instance: " + response.message);
            }
        });
    }

    /*
     * Performs a POST on the given url, passing the given data (if any) as the Body post
     * @param Url - Url of the API to hit
     * @param Data - The JSON object the post as the body parameters
     * @returns response parsed as T Type
    */
    async postRequest<T>(Url: string, Data?: any): Promise<T> {
        const response = await fetch("https://physicsclassroom.hbstest.net" + Url, {
            method: "POST",
            mode: 'cors',
            credentials: "include",
            cache: "no-cache",
            headers: {
                "Authorization": "Bearer " + this.sessionId,
                'Content-Type': 'application/json'
            },
            body: (Data ? JSON.stringify(Data) : "")
        });
        return await response.json();
    }

    /*
    * Performs a GET on the given url, passing any data records as query parameters, and parsing the response as T object type
    * @param Url - Url of the API to hit
    * @param Data - Key (string) value (string, number, or boolean) of any parameters to include in the call, these are parsed as URL variables    
    * @returns response parsed as T Type
   */
    async getRequest<T>(Url: string, Data?: Record<string, (string | number | boolean)>): Promise<T> {
        var urlWithParams = "https://physicsclassroom.hbstest.net" + Url;
        if (Data) {
            let params = new Array<string>();
            for (let key in Data) {
                params.push(encodeURI(key) + "=" + encodeURI(Data[key].toString()))
            }
            urlWithParams += "?" + params.join("&");
        }
        const response = await fetch(urlWithParams, {
            method: "GET",
            mode: 'cors',
            credentials: "include",
            cache: "no-cache",
            headers: {
                "Authorization": "Bearer " + this.sessionId,
                'Content-Type': 'application/json'
            }
        });
        return await response.json();
    }

    /*
     * Runs the given promise calls in order against the provided array of values, waiting for each item to finish it's promise before executing the next
     * @param values - Array of values that will execute in the funcToRun in order
     * @param funcToRun - Function that takes the single value and returns a Promise<TPromise> of the asynchronous call (ex postRequest or getRequest of this helper)
     * @param funcAfterEachRun - Optional function that receives the Promise's result, if you return false then aborts the rest of the calls, nothing / true continues
     * @param funcWhenFinished - Optional function that runs once every item is executed.
    */
    public runInSequence<T, TPromise>(values: Array<T>, funcToRun: (value: T) => Promise<TPromise>, funcAfterEachRun?: (value: TPromise) => boolean | any | undefined, funcWhenFinished?: () => any | undefined): void {
        console.log("Cannot use RunInSequence method for testing instance of ajax helper.");
        if (typeof funcWhenFinished !== undefined) {
            funcWhenFinished!!();
        }
        return;
    }
}

export class AjaxCaller {
    /*
     * Performs a POST on the given url, passing the given data (if any) as the Body post
     * @param Url - Url of the API to hit
     * @param Data - The JSON object the post as the body parameters
     * @returns response parsed as T Type
    */
    static async postRequest<T>(Url: string, Data?: any): Promise<T> {
        const helper = new AjaxHelper();
        return helper.postRequest<T>(Url, Data);
    }

    static async postRequestExcelFile(Url: string, FileName: string, Data?: any): Promise<void> {
        const helper = new AjaxHelper();
        var sanitizedFilename = FileName.replace(/[\\/:*?"<>|\.]/g, '');
        return helper.postRequestFile(Url, Data)
            .then(blob => {
                var file = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                console.log(file);
                var fileURL = URL.createObjectURL(file);

                // create <a> tag dinamically
                var fileLink = document.createElement('a');
                fileLink.href = fileURL;

                // it forces the name of the downloaded file
                fileLink.download = sanitizedFilename;

                // triggers the click event
                fileLink.click();
            });
    }

    /*
    * Performs a GET on the given url, passing any data records as query parameters, and parsing the response as T object type
    * @param Url - Url of the API to hit
    * @param Data - Key (string) value (string, number, or boolean) of any parameters to include in the call, these are parsed as URL variables    
    * @returns response parsed as T Type
   */
    static async getRequest<T>(Url: string, Data?: Record<string, (string | number | boolean)>): Promise<T> {
        const helper = new AjaxHelper();
        return helper.getRequest<T>(Url, Data);
    }
}

export class AjaxHelper implements IAjaxHelper {

    constructor() {

    }

    /*
     * Performs a POST on the given url, passing the given data (if any) as the Body post
     * @param Url - Url of the API to hit
     * @param Data - The JSON object the post as the body parameters
     * @returns response parsed as T Type
    */
    async postRequest<T>(Url: string, Data?: any): Promise<T> {
        const response = await fetch(Url, {
            method: "POST",
            mode: 'cors',
            cache: "no-cache",
            headers: {
                'Content-Type': 'application/json'
            },
            body: (Data ? JSON.stringify(Data) : "")
        });
        if (!response.ok) {
            var json = await response.json();
            throw json.message ?? json.Message;
        }
        return await response.json();
    }

    /*
     * Performs a POST on the given url, passing the given data (if any) as the Body post
     * @param Url - Url of the API to hit
     * @param Data - The JSON object the post as the body parameters
     * @returns response parsed as T Type
    */
    async postRequestFile(Url: string, Data?: any): Promise<Blob> {
        const response = await fetch(Url, {
            method: "POST",
            mode: 'cors',
            cache: "no-cache",
            headers: {
                'Content-Type': 'application/json',
            },
            body: (Data ? JSON.stringify(Data) : "")
        });
        if (!response.ok) {
            var json = await response.json();
            throw json.message;
        }
        console.log(response);
        return await response.blob();
    }

    /*
    * Performs a GET on the given url, passing any data records as query parameters, and parsing the response as T object type
    * @param Url - Url of the API to hit
    * @param Data - Key (string) value (string, number, or boolean) of any parameters to include in the call, these are parsed as URL variables    
    * @returns response parsed as T Type
   */
    async getRequest<T>(Url: string, Data?: Record<string, (string | number | boolean)>): Promise<T> {
        var urlWithParams = Url;
        if (Data) {
            let params = new Array<string>();
            for (let key in Data) {
                params.push(encodeURI(key) + "=" + encodeURI(Data[key].toString()))
            }
            urlWithParams += "?" + params.join("&");
        }
        const response = await fetch(urlWithParams, {
            method: "GET",
            mode: 'cors',
            cache: "no-cache"
        });
        if (!response.ok) {
            var json = await response.json();
            throw json.message ?? json.Message;
        }
        return await response.json();
    }

    /*
     * Runs the given promise calls in order against the provided array of values, waiting for each item to finish it's promise before executing the next
     * @param values - Array of values that will execute in the funcToRun in order
     * @param funcToRun - Function that takes the single value and returns a Promise<TPromise> of the asynchronous call (ex postRequest or getRequest of this helper)
     * @param funcAfterEachRun - Optional function that receives the Promise's result, if you return false then aborts the rest of the calls, nothing / true continues
     * @param funcWhenFinished - Optional function that runs once every item is executed.
    */
    public runInSequence<T, TPromise>(values: Array<T>, funcToRun: (value: T) => Promise<TPromise>, funcAfterEachRun?: (value: TPromise) => boolean | any | undefined, funcWhenFinished?: () => any | undefined): void {
        if (values.length > 0) {
            var value = values[0];
            values = values.splice(1);
            funcToRun(value).then((tPromise: TPromise) => {
                if (typeof funcAfterEachRun !== undefined && funcAfterEachRun != null) {
                    var result = funcAfterEachRun(tPromise);
                    if (typeof result === "boolean" && !(result as boolean)) {
                        // Abort
                        return;
                    }
                }
                this.runInSequence(values, funcToRun, funcAfterEachRun, funcWhenFinished);
            });
        } else {
            if (typeof funcWhenFinished !== undefined) {
                funcWhenFinished!!();
            }
        }
        return;
    }
}